import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import ellipseImage from '../images/Ellipse 9.png';
import { DisplayH1, GradientWord } from '../components/Headlines';
import { MainBtn } from '../components/Btns';
import { useContact } from '../contexts/ContactContext';

const StyledHero = styled.section`
    background-color: ${props => props.theme.darkBg1};
    position: relative;
    padding-top: 140px;
    padding-bottom: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`;

const ContentBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 760px;
    padding: 40px 30px 0;
    text-align: center;
`;

const Subhead = styled.p`
    font-family: ${props => props.theme.lightFont};
    font-size: 19px;
    font-weight: 300;
    line-height: 1.6;
    color: ${props => props.theme.textMuted};
    margin: 24px 0 36px;
    max-width: 560px;
`;

const CtaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: center;
    align-items: center;
`;

const GhostBtn = styled.button`
    padding: 9px 33px;
    background: transparent;
    border: 1px solid ${props => props.theme.hairline};
    border-radius: 33px;
    color: ${props => props.theme.textColor};
    font-family: ${props => props.theme.regularFont};
    font-size: 20px;
    font-weight: 400;
    line-height: 36px;
    cursor: pointer;
    transition: border-color 0.3s ease;

    &:hover {
        border-color: ${props => props.theme.accentBorder};
    }
`;

const VideoWrap = styled.div`
    margin-top: 8px;
    @media screen and (min-width: 800px) {
        max-width: 560px;
    }
`;

const TrustStrip = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    margin-top: 30px;
    padding: 0 20px;
`;

const TrustLabel = styled.span`
    font-family: ${props => props.theme.regularFont};
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${props => props.theme.textMuted};
`;

const TrustLogos = styled.div`
    display: flex;
    align-items: center;
    gap: 40px;
    flex-wrap: wrap;
    justify-content: center;
`;

const TrustName = styled.span`
    font-family: ${props => props.theme.regularFont};
    font-size: 22px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
`;

export default function Hero() {
    const videoRef = useRef(null);
    const { openContact } = useContact();

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleVideoEnd = () => {
            video.playbackRate = -1;
            video.play();
        };

        const handleTimeUpdate = () => {
            if (video.currentTime <= 0.1 && video.playbackRate < 0) {
                video.playbackRate = 1;
                video.play();
            }
        };

        video.addEventListener('ended', handleVideoEnd);
        video.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            video.removeEventListener('ended', handleVideoEnd);
            video.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, []);

    return (<StyledHero>
            <img
                style={{ position: "absolute", top: "60px", right: "0", zIndex: "0", opacity: 0.6 }}
                src={ellipseImage}
                alt={""} />
            <ContentBox>
                <DisplayH1>{`Never stuck.\n`}<GradientWord>Never waiting.</GradientWord></DisplayH1>
                <Subhead>
                    We design and build websites for B2B tech teams - then keep them
                    fast, secure, and found. Ranking in Google, cited in AI answers,
                    and built to convert.
                </Subhead>
                <CtaRow>
                    <MainBtn onClick={() => openContact()}>Let's talk</MainBtn>
                    <GhostBtn onClick={() => { window.location.hash = '#projects'; }}>See our work</GhostBtn>
                </CtaRow>
            </ContentBox>
            <VideoWrap>
                <video
                    ref={videoRef}
                    width="100%"
                    height="auto"
                    autoPlay
                    loop
                    muted
                    playsInline>
                    <source src={"/abstract-donut.mp4"} type="video/mp4" />
                </video>
            </VideoWrap>
            <TrustStrip>
                <TrustLabel>Trusted by teams at</TrustLabel>
                <TrustLogos>
                    <TrustName>XM Cyber</TrustName>
                    <TrustName>Webz</TrustName>
                </TrustLogos>
            </TrustStrip>
    </StyledHero>);
}
